import { cn } from '../../lib/cn'
import { ClubLinkButton } from './ClubButton'
import type { MembershipTier } from '../../lib/membership'

interface TierCardProps {
  tier: MembershipTier
  /** Renders the pine emphasis treatment + "Most chosen" ribbon. */
  featured?: boolean
  /** Where the CTA goes. */
  to: string
  ctaLabel: string
  /**
   * The CTA's weight. Defaults to the card's own reading — `flag` when
   * featured, `ghost` otherwise. The homepage hands its founding card `ink`
   * (homepage.html:228, `.btn-ink`): the pine second-weight CTA, so the seat
   * is claimed in a button that is neither the page's one flag nor a ghost.
   */
  ctaVariant?: 'flag' | 'ink' | 'ghost'
  className?: string
}

/**
 * The tier this card is selling, carried to wherever its CTA lands. Without it
 * all three cards were an identical `to="/join"` and the choice was lost the
 * moment it was made — checkout then fell back to a default and charged for a
 * tier nobody picked.
 */
function tierSearch(tier: MembershipTier) {
  return { tier: tier.id }
}

/**
 * A membership tier, as the shop window for the club (homepage + membership
 * cards). `featured` flips the card to pine and adds the brass ribbon.
 *
 * Included/excluded features are marked with a filled vs hollow brass ring in
 * the design. A ring is invisible to a screen reader, so each item also carries
 * a visually-hidden "Included"/"Not included" — otherwise the two lists read
 * identically.
 */
export function TierCard({
  tier,
  featured = false,
  to,
  ctaLabel,
  ctaVariant,
  className,
}: TierCardProps) {
  return (
    <div
      className={cn(
        'relative flex flex-col rounded-club-lg border px-7.5 py-8.5',
        featured
          ? 'on-pine border-pine bg-pine shadow-[0_24px_60px_-30px_rgba(21,58,42,0.6)]'
          : 'border-ink/14 bg-parchment',
        className,
      )}
    >
      {featured && (
        <span className="absolute -top-3 left-7.5 rounded-club-sm bg-brass-deep px-3 py-[5px] text-[10.5px] font-bold uppercase tracking-[0.16em] text-parchment">
          Most chosen
        </span>
      )}

      <h3 className={cn('font-display text-[23px] font-medium', featured ? 'text-paper' : 'text-pine')}>
        {tier.name}
      </h3>

      <p className={cn('mb-0.5 mt-4 font-display text-[42px] leading-none', featured ? 'text-parchment' : 'text-pine')}>
        {tier.price}
        <span
          className={cn(
            'align-baseline font-text text-[13px] font-medium',
            featured ? 'text-sage' : 'text-ink-soft',
          )}
        >
          /{tier.interval}
        </span>
      </p>

      <ul className="mb-7 mt-5.5 flex flex-col gap-3">
        {tier.features.map((feature) => (
          <li
            key={feature.label}
            className={cn('relative pl-6 text-sm', featured ? 'text-paper/90' : 'text-ink')}
          >
            <span
              aria-hidden="true"
              className={cn(
                'absolute left-0 top-[7px] size-[9px] rounded-full border-[1.5px] border-brass',
                feature.included && 'bg-brass',
              )}
            />
            <span className="sr-only">{feature.included ? 'Included: ' : 'Not included: '}</span>
            {feature.label}
          </li>
        ))}
      </ul>

      {tier.soldOut ? (
        // A finite tier that has run out. The card says so instead of offering
        // a CTA that checkout would refuse — see club-content-inventory.md §4
        // on controls that lie.
        <p className="mt-auto w-full rounded-club-sm border border-dashed border-brass/45 px-4 py-3 text-center text-[13px] text-ink-soft">
          All one hundred struck
        </p>
      ) : (
        <ClubLinkButton
          to={to}
          search={tierSearch(tier)}
          variant={ctaVariant ?? (featured ? 'flag' : 'ghost')}
          className="mt-auto w-full"
        >
          {ctaLabel}
        </ClubLinkButton>
      )}
    </div>
  )
}
